<template>
  <wui-list-item
    icon="image"
    @click="copyImage"
  >
    {{ $t('common.copyImage') }}
  </wui-list-item>
</template>

<script setup lang="ts">
import { useRenderNotifier, WuiListItem } from '@wui/common-library'

import { useLocalization } from '@/shared/composables/localization'
import { fetchAuthenticatedMediaBlob } from '@/shared/utils/url'

const props = defineProps<{
  fileCookie: string
}>()

const { notifyError, notifySuccess } = useRenderNotifier()
const { t } = useLocalization()

const copyImage = async () => {
  try {
    if (!navigator.clipboard?.write || !window.ClipboardItem) {
      throw new Error(t('common.unknownError'))
    }

    const responseBlob = await fetchAuthenticatedMediaBlob(`/files/${props.fileCookie}`)
    const clipboardMimeType = 'image/png'
    const imageBlob = await toBlobWithMimeType(responseBlob, clipboardMimeType)

    await navigator.clipboard.write([
      new ClipboardItem({
        [clipboardMimeType]: imageBlob,
      }),
    ])

    notifySuccess(t('mainAside.copiedToClipboard'))
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
    notifyError(errorMessage)
  }
}

const toBlobWithMimeType = async (blob: Blob, mimeType: string) => {
  if (blob.type === mimeType) {
    return blob
  }

  const imageBuffer = await blob.arrayBuffer()
  return new Blob([imageBuffer], { type: mimeType })
}
</script>
